import { weekOptions } from 'app/data/static-load/model';
import { Form, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { staticLoadSelector } from 'slices/static-load/staticLoadSlice';
import Button, { ButtonThemes } from 'ui-components/button/button';
import XGSFormSelect from 'ui-components/form/select/xgsFormSelect';
import ImportantMessage, { ImportantMessageTheme } from 'ui-components/molecules/important-message/importantMessage';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import XGSFormDate from 'ui-components/form/date/xgsFormDate';
import { currentOrClosestBusinessDay } from 'hooks/utils';
import { useLaneByPlanID, useMoveLane } from '../hooks/useLanes';
import moment from 'moment';
import SourceDispatchCard from '../components/sourceDispatchCard';

interface CopyMoveLaneSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  copyLaneToDayMutation: any;
  type: 'copy' | 'move';
  refetchLaneByDay: () => void;
}

const CopyMoveLaneSidebar: React.FC<CopyMoveLaneSidebarProps> = ({ isOpen,
  onClose, copyLaneToDayMutation, type, refetchLaneByDay }) => {
    
  const [targetWeekStartDate, setTargetWeekStartDate] = useState<string | undefined>();

  const staticLoadSelectorData = useSelector(staticLoadSelector);
  const selectedDivision = staticLoadSelectorData.selectedDivision;
  const weekStartDate = staticLoadSelectorData.weekStartDate;
  const selectedPlanId = staticLoadSelectorData.selectedPlanId;

  const { data: laneByPlanId } = useLaneByPlanID(
    selectedPlanId, selectedDivision, weekStartDate)

  const moveLaneMutation = useMoveLane();

  return <SlideOutSidebar
    show={isOpen}
    onClose={onClose}
    header={type === 'copy' ? 'Duplicate Dispatch' : 'Move Lane'}
    size="small"
    className="filter-sidebar"
  >
    {type === 'copy' ? <Formik
      initialValues={{
        targetDay: (laneByPlanId?.lane?.dayOfWeek || 0) - 1,
      }} onSubmit={(values: any) => {
        copyLaneToDayMutation.mutate({
          division: selectedDivision,
          weekStartDate: moment(weekStartDate).format('YYYY-MM-DD'),
          sourceDay: laneByPlanId?.lane?.dayOfWeek,
          turnNumber: laneByPlanId?.lane?.turnNumber?.toString() || "",
          targetDay: values.targetDay.toString(),
          planId: selectedPlanId?.toString() || "",
        }, {
          onSuccess: () => {
            refetchLaneByDay();
            onClose();
          },
        });
      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<any>) => (
        <Form>
          <SourceDispatchCard
            label={"Source Dispatch"}
            turnNumber={laneByPlanId?.lane?.turnNumber?.toString() || "—"}
            carrierName={laneByPlanId?.lane?.carrierName || "—"}
            carrierNumber={laneByPlanId?.lane?.carrierNumber?.toString()}
            date={moment(laneByPlanId?.lane?.weekStartDate).add((laneByPlanId?.lane?.dayOfWeek || 0) - 1, 'days').format('MM/DD/YYYY') || "—"}
          />
          <div style={{ marginBottom: "10px" }} />
          <ImportantMessage theme={ImportantMessageTheme.BLUE}>
            Original stays. A copy is created on the target day.
          </ImportantMessage>
          <div style={{ marginBottom: "10px" }} />
          <XGSFormSelect
            label="Target Day"
            name="targetDay"
            options={weekOptions}
            value={
              formikProps.values.targetDay
                ? weekOptions.find((option) => option.value === formikProps.values.targetDay)
                : null
            }
            onValueChange={(option) => {
              formikProps.setFieldValue("targetDay", option?.value ?? "");
            }}
            formik
            labelMode={LabelModes.column}
          />
          <div style={{ marginTop: "10px" }} />
          <div className="copy-move-lane-actions">
            <Button type="button" theme={ButtonThemes.gray} onClick={onClose}>Cancel</Button>
            <Button type="submit" theme={ButtonThemes.blue} spinner={copyLaneToDayMutation.isLoading} disabled={!formikProps.dirty}>Confirm</Button>
          </div>
        </Form>
      )}
    </Formik> : null}
    {type === 'move' ? <Formik
      initialValues={{
        targetWeekStartDate: "",
      }} onSubmit={() => {
        if (!targetWeekStartDate) return;
        moveLaneMutation.mutate({
          planId: staticLoadSelectorData.selectedPlanId,
          targetWeekStartDate: moment(targetWeekStartDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
        }, {
          onSuccess: () => {
            refetchLaneByDay();
            onClose();
          },
        });
      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<any>) => (
        <Form>
          <SourceDispatchCard
            label={"Move Dispatch"}
            turnNumber={laneByPlanId?.lane?.turnNumber?.toString() || "—"}
            carrierName={laneByPlanId?.lane?.carrierName || "—"}
            carrierNumber={laneByPlanId?.lane?.carrierNumber?.toString()}
            date={moment(laneByPlanId?.lane?.weekStartDate).add((laneByPlanId?.lane?.dayOfWeek || 0) - 1, 'days').format('MM/DD/YYYY') || "—"}
            departureTime={laneByPlanId?.lane?.departureTime || "—"}
          />
          <div style={{ marginBottom: "10px" }} />
          <ImportantMessage theme={ImportantMessageTheme.BLUE}>
            The lane will be removed from the source day and moved to the target day.
          </ImportantMessage>
          <div style={{ marginBottom: "10px" }} />
          <XGSFormDate
            name="targetWeekStartDate"
            label="Target Week Start Date:"
            placeholder="Select date..."
            onDateChange={(date) => { setTargetWeekStartDate(date) }}
            value={targetWeekStartDate}
            onChange={() => null}
            minDate={new Date(currentOrClosestBusinessDay())}
            required={true}
            labelMode={LabelModes.column}
            filterDate={(date) => date.getDay() === 1}
            disableWeekends={true}
          // disabledWeekDays={type === "EMPLOYEE" ? {} : commonState.pickupDays}
          // disabledDates={type === "EMPLOYEE" ? [] : getHolidayDates()}
          // disabled={commonState.requestStarted && (commonState.requestCreator === "GET_PICKUP_DAYS")}
          // dayClassName={(date) => getDateClassName(date, commonState.pickupDays, getHolidayDates())}
          />
          <div style={{ marginTop: "10px" }} />
          <div className="copy-move-lane-actions">
            <Button type="button" theme={ButtonThemes.gray} onClick={onClose}>Cancel</Button>
            <Button type="submit" theme={ButtonThemes.blue} spinner={moveLaneMutation.isLoading} disabled={!targetWeekStartDate}>Confirm</Button>
          </div>
        </Form>
      )}
    </Formik> : null}


  </SlideOutSidebar>;
};

export default CopyMoveLaneSidebar;
